import { FunctionComponent, useEffect, useState } from 'react';

import { Button } from '@client:common/button';
import { Ripple } from '@client:common/effects/ripple';
import { PortalRenderer } from '@client:common/overlay/portal-renderer';

import './Notification.light.scss';
import './Notification.dark.scss';

interface Props {
  show?: boolean;
  onHide?: () => void;
  children: React.ReactNode;
}

export function Notification(props: Props) {
  const animationDuration = 500;
  const visibilityDuration = 30_000;
  const [isVisible, setIsVisible] = useState(props.show);
  const [scheduler, setScheduler] = useState(-Infinity);
  const hideNotification = () => {
    if (props.onHide) {
      window.setTimeout(props.onHide, animationDuration);
    }
    setIsVisible(false);
  };

  useEffect(() => {
    if (props.show) {
      setIsVisible(true);
      setScheduler(window.setTimeout(hideNotification, visibilityDuration));
    } else {
      window.clearTimeout(scheduler);
      hideNotification();
    }
    return () => {
      window.clearTimeout(scheduler);
    };
  }, [props.show]);

  useEffect(() => {
    const onEscapeKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        hideNotification();
      }
    };
    window.addEventListener('keyup', onEscapeKeyPress, false);
    return () => {
      window.removeEventListener('keyup', onEscapeKeyPress, false);
    };
  }, []);

  return (
    <div
      className={`notification-container ${isVisible ? 'visible' : 'hidden'}`}
      onPointerUp={hideNotification}>
      <div className='notification'>
        {props.children}
        <Button
          className='notification__close'
          style='basic'
          color='primary'>
          <Ripple />
          Close
        </Button>
      </div>
    </div>
  );
}

(Notification as FunctionComponent<Props>).defaultProps = {
  show: true
};
Notification.open = (content: React.ReactChild) => {
  return new Promise<void>(resolve => {
    const portalRenderer = new PortalRenderer();
    portalRenderer.mount(
      <Notification
        onHide={() => {
          resolve();
          portalRenderer.unmount();
        }}>
        {content}
      </Notification>
    );
  });
};
